import React, { useRef, useState, useEffect } from "react";
import { FaArrowLeft, FaArrowRight, FaCalendarAlt } from "react-icons/fa";
import { useDateSelection } from "../../contexts/DateSelectionProvider";
import { useTranslation } from "react-i18next";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const EventDates = ({ option }) => {
  const { dates, selectedUrl, selectDate } = useDateSelection();
  const containerRef = useRef(null);
  const dateRefs = useRef({});
  const { t } = useTranslation();
  const [activeDate, setActiveDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);

  // Automatically select the first date when data is available
  useEffect(() => {
    if (dates.length > 0 && !activeDate) {
      setActiveDate(dates[0].date);
      selectDate(dates[0].date);
    }
  }, [dates, activeDate, selectDate]);

  // Auto-scroll to active date whenever activeDate changes
  useEffect(() => {
    if (activeDate && dateRefs.current[activeDate] && containerRef.current) {
      const activeDateElement = dateRefs.current[activeDate];
      const container = containerRef.current;
      
      // Calculate scroll position to center the active date
      const elementLeft = activeDateElement.offsetLeft;
      const elementWidth = activeDateElement.offsetWidth;
      const containerWidth = container.offsetWidth;
      const scrollPosition = elementLeft - (containerWidth / 2) + (elementWidth / 2);
      
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  }, [activeDate]);

  const handleNext = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: containerRef.current.clientWidth - 100,
        behavior: "smooth",
      });
    }
  };

  const handlePrev = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: -(containerRef.current.clientWidth - 100),
        behavior: "smooth",
      });
    }
  };

  // Convert `dd-mm-yyyy` to Date object
  const parseDate = (dateString) => {
    const [day, month, year] = dateString.split('-');
    return new Date(year, month - 1, day);
  };

  // Convert Date object to `dd-mm-yyyy` format
  const formatDateToDDMMYYYY = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Sort dates
  const sortedDates = [...dates].sort((a, b) => parseDate(a.date) - parseDate(b.date));

  const handleDateClick = (date) => {
    setActiveDate(date);
    selectDate(date);
  };

  // Check if a date has data
  const hasData = (calendarDate) => {
    const formattedCalendarDate = formatDateToDDMMYYYY(calendarDate);
    return dates.some(match => match.date === formattedCalendarDate);
  };




  // Handle calendar date click
  const handleCalendarDateClick = (value) => {
    if (hasData(value)) {
      const formattedDate = formatDateToDDMMYYYY(value);
      handleDateClick(formattedDate);
      setShowCalendar(false);
    }
  };

  // Tile className for highlighting dates with data
  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      if (hasData(date)) {
        return 'highlighted-date';
      }
    }
    return null;
  };

  // Disable dates without data
  const tileDisabled = ({ date, view }) => {
    if (view === 'month') {
      return !hasData(date);
    }
    return false;
  };

  // Get the active date as Date object for calendar value
  const getActiveDateObject = () => {
    if (activeDate) {
      const [day, month, year] = activeDate.split('-');
      return new Date(year, month - 1, day);
    }
    return null;
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <div className="flex justify-between items-center w-full relative">
        {/* Left Arrow */}
        {dates.length > 0 ? (
          <button onClick={handlePrev} className="arrow-btn tooltip tooltip-right"
            data-tip={t("Previous Dates")}
            style={{ backgroundColor: '#4CAF50', color: 'white', borderRadius: '8px', padding: '8px', transition: 'all 0.3s' }}>
            <FaArrowLeft className="text-sm sm:text-[16px] lg:text-lg 2xl:text-2xl" />
          </button>
        ) : (
          <button className="arrow-btn tooltip tooltip-right"
            data-tip={t("no dates available")}
            style={{ backgroundColor: '#9E9E9E', color: 'white', borderRadius: '8px', padding: '8px' }}>
            <FaArrowLeft className="text-sm sm:text-[16px] lg:text-lg 2xl:text-2xl" />
          </button>
        )}

        {/* Dates Scroll Container */}
        {dates.length > 0 ? (
          <div
            ref={containerRef}
            className="flex overflow-x-auto scroll-smooth mx-2 custom-scrollbar"
            style={{
              scrollbarWidth: 'thin',
              msOverflowStyle: 'auto',
              gap: '8px',
              padding: '4px 0'
            }}
          >
            {sortedDates.map((match, index) => (
              <div
                key={index}
                ref={el => dateRefs.current[match.date] = el}
                onClick={() => handleDateClick(match.date)}
                className={`flex-shrink-0 px-4 py-2 text-sm sm:text-[16px] cursor-pointer font-medium rounded-lg transition-all duration-300
                ${activeDate === match.date 
                  ? "bg-gradient-to-r from-green-600 to-green-700 text-white shadow-lg transform scale-105" 
                  : "bg-white hover:bg-green-100 text-gray-700 border border-green-200 hover:shadow-md"}`}
                style={{ margin: '0 4px' }}
              >
                {match.date}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600 font-medium text-sm mx-1">{t("No dates available")}</p>
        )}

        {/* Calendar Icon Button */}
        {dates.length > 0 && (
          <div className="relative " ref={calendarRef}>
            <button
              onClick={() => setShowCalendar(!showCalendar)}
              className="arrow-btn tooltip"
              data-tip={t("Calendar View")}
              style={{ backgroundColor: '#4CAF50', color: 'white', borderRadius: '8px', padding: '8px', transition: 'all 0.3s' }}
            >
              <FaCalendarAlt className="text-sm sm:text-[16px] lg:text-lg 2xl:text-2xl" />
            </button>

            {/* React Calendar Popup */}
            {showCalendar && (
              <div className="absolute left-24  z-50 bg-white rounded-lg shadow-xl border p-1"
                style={{ top: '100%', minWidth: '350px' }}>
                <Calendar
                  onChange={handleCalendarDateClick}
                  value={getActiveDateObject()}
                  tileClassName={tileClassName}
                  tileDisabled={tileDisabled}
                  minDate={new Date(2020, 0, 1)}
                  maxDate={new Date(2030, 11, 31)}
                />
              </div>
            )}
          </div>
        )}

        {/* Right Arrow */}
        {dates.length > 0 ? (
          <button onClick={handleNext} className="arrow-btn tooltip ml-2"
            data-tip={t("Next Dates")}
            style={{ backgroundColor: '#4CAF50', color: 'white', borderRadius: '8px', padding: '8px', transition: 'all 0.3s' }}>
            <FaArrowRight className="text-sm sm:text-[16px] lg:text-lg 2xl:text-2xl" />
          </button>
        ) : (
          <button className="arrow-btn tooltip ml-2"
            data-tip={t("no dates available")}
            style={{ backgroundColor: '#9E9E9E', color: 'white', borderRadius: '8px', padding: '8px' }}>
            <FaArrowRight className="text-sm sm:text-[16px] lg:text-lg 2xl:text-2xl" />
          </button>
        )}
      </div>

      {/* Display selected URL */}
      <div className="mt-2 md:mt-1 hidden">
        {selectedUrl ? (
          <p className="text-gray-700 font-medium text-sm mx-1">
            {t("Selected URL")}: {selectedUrl}
          </p>
        ) : (
          <p className="text-gray-700 font-medium text-sm mx-1">
            {t("Select a date to view the associated URL.")}
          </p>
        )}
      </div>

      <style jsx>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #4CAF50 #E8F5E9;
          max-width: calc(100% - 100px);
          flex: 1;
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          height: 8px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #E8F5E9;
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(45deg, #4CAF50, #45a049);
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(45deg, #45a049, #3d8b40);
        }
        
        /* Hide scrollbar when not hovering on desktop */
        @media (min-width: 768px) {
          .custom-scrollbar {
            scrollbar-width: thin;
          }
        }
        
        /* React Calendar custom styles */
        /* Highlighted dates (dates with data) - Light Yellow */
        .highlighted-date {
          background: #FEF9C3 !important;
          color: #854D0E !important;
          border-radius: 50% !important;
          font-weight: bold !important;
          border: 1px solid #FDE047 !important;
        }
        
        .highlighted-date:hover {
          background: #FDE047 !important;
          transform: scale(1.05) !important;
          transition: transform 0.2s !important;
        }
        
        /* Active/Selected date in calendar - Green */
        .react-calendar__tile--active {
          background: #4CAF50 !important;
          color: white !important;
          border-radius: 50% !important;
        }
        
        /* When active date is also a highlighted date */
        .react-calendar__tile--active.highlighted-date {
          background: #4CAF50 !important;
          color: white !important;
        }
        
        /* Today's date */
        .react-calendar__tile--now {
          background: #E8F5E9 !important;
          border-radius: 50% !important;
          border: 2px solid #4CAF50 !important;
        }
        
        /* Today's date when it's also active */
        .react-calendar__tile--now.react-calendar__tile--active {
          background: #4CAF50 !important;
          color: white !important;
          border: 2px solid #2E7D32 !important;
        }
        
        /* Today's date when it's highlighted */
        .react-calendar__tile--now.highlighted-date {
          background: #FEF9C3 !important;
          color: #854D0E !important;
          border: 2px solid #4CAF50 !important;
        }
        
        /* Calendar main container */
        .react-calendar {
          border: none;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          background: white;
        }
        
        /* Calendar navigation buttons */
        .react-calendar__navigation button {
          color: #2E7D32;
          font-weight: bold;
        }
        
        .react-calendar__navigation button:enabled:hover,
        .react-calendar__navigation button:enabled:focus {
          background-color: #E8F5E9;
          border-radius: 8px;
        }
        
        /* Calendar tiles (dates) */
        .react-calendar__tile {
          padding: 10px;
          border-radius: 8px;
          transition: all 0.2s;
        }
        
        .react-calendar__tile:enabled:hover,
        .react-calendar__tile:enabled:focus {
          background-color: #F3F4F6;
          border-radius: 50%;
        }
        
        /* Disabled dates (no data) */
        .react-calendar__tile:disabled {
          background-color: #F9FAFB;
          color: #D1D5DB;
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        /* Weekday headers */
        .react-calendar__month-view__weekdays {
          color: #2E7D32;
          font-weight: bold;
        }
        
        .react-calendar__month-view__weekdays abbr {
          text-decoration: none;
        }
        
        /* Button hover effects */
        .arrow-btn {
          transition: all 0.3s ease;
        }
        
        .arrow-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
        }
        
        .arrow-btn:active {
          transform: scale(0.95);
        }
      `}</style>
    </>
  );
};

export default EventDates;